import { ArrowBack, PaidOutlined } from "@mui/icons-material";
import { Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import { useState } from "react";
import RelatedJournalEntries from "@/components/accounting/RelatedJournalEntries";
import RecordPayoutDrawer from "@/components/finance/RecordPayoutDrawer";
import { SOURCE_TYPE_PURCHASE } from "@/constants/accounting";
import { PURCHASE_STATUS_CHIP } from "@/constants/purchases";
import { usePermissions } from "@/hooks/usePermissions";
import { useClientPurchase, usePurchaseEvents, useRecordPurchasePaid } from "@/hooks/usePurchases";
import { formatDateTime } from "@/utils/date-format";
import { formatAmount } from "@/utils/number-format";

const money = (v: string | null, ccy = "USD") =>
  v == null ? "—" : `${ccy} ${formatAmount(Number(v))}`;

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          color: "var(--color-graphite)",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ fontSize: 14, fontWeight: 500 }}>{children}</Box>
    </Box>
  );
}

/**
 * One client credit purchase. Record Paid appears only while the purchase is in `clearing` — the
 * card cleared but Stripe's payout hasn't been seen — and only for someone who can act on money.
 */
/**
 * A purchase's activity feed. The events were already being written — payout recorded, payment
 * cleared — with nowhere to read them back.
 */
function PurchaseActivityLog({ purchaseId }: { purchaseId: number }) {
  const { data: events = [], isLoading } = usePurchaseEvents(purchaseId);

  return (
    <Box sx={{ mt: 4 }}>
      <Typography
        sx={{
          fontSize: 11,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          fontWeight: 600,
          color: "var(--color-tan, #a98d6b)",
          mb: 1.5,
        }}
      >
        Activity
      </Typography>
      {isLoading ? (
        <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>Loading…</Typography>
      ) : events.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
          No activity yet.
        </Typography>
      ) : (
        events.map((e, i) => (
          <Box key={e.id} sx={{ display: "flex", gap: 1.5 }}>
            {/* rail: dot + connecting line, matching the invoice timeline */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: "5px" }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "var(--color-rodeo-black)",
                  flexShrink: 0,
                }}
              />
              {i < events.length - 1 && (
                <Box sx={{ width: "1px", flex: 1, bgcolor: "var(--color-smoke)", my: "2px" }} />
              )}
            </Box>
            <Box sx={{ pb: 2 }}>
              <Typography sx={{ fontSize: 14, color: "var(--color-rodeo-black)" }}>
                {e.statusDescription ?? e.eventType}
                <Box component="span" sx={{ color: "var(--color-graphite)" }}>
                  {/* No actor means a webhook or a cron did it. */}
                  {" — "}
                  {e.actorName ?? "System"}
                </Box>
              </Typography>
              <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
                {formatDateTime(e.createdAt)}
              </Typography>
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
}

export default function PurchaseDetail({
  purchaseId,
  onBack,
  backLabel = "Client Purchases",
}: {
  purchaseId: number;
  onBack: () => void;
  backLabel?: string;
}) {
  const { can } = usePermissions();
  const canRecord = can("cancel", "FINANCE");
  const { data: p, isLoading } = useClientPurchase(purchaseId);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const record = useRecordPurchasePaid(purchaseId);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress aria-label="Loading" size={28} />
      </Box>
    );
  }
  if (!p) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ textTransform: "none" }}>
          {backLabel}
        </Button>
        <Typography sx={{ mt: 2 }}>Purchase not found.</Typography>
      </Box>
    );
  }

  const chip = PURCHASE_STATUS_CHIP[p.settlementStatus];
  const isClearing = p.settlementStatus === "clearing";

  return (
    <Box sx={{ fontFamily: "var(--font-lato)" }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={onBack}
        sx={{ textTransform: "none", color: "var(--color-graphite)", mb: 2 }}
      >
        {backLabel}
      </Button>

      <Box
        sx={{
          border: "1px solid var(--color-card-border)",
          borderRadius: "var(--radius-card)",
          p: { xs: 2, md: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            mb: 2.5,
          }}
        >
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
            {p.purchaseNumber ?? `Purchase #${p.id}`}
          </Typography>
          <Chip size="small" variant="outlined" label={chip.label} color={chip.color} />
          <Box sx={{ flex: 1 }} />
          {isClearing && canRecord && (
            <Button
              variant="contained"
              startIcon={<PaidOutlined />}
              onClick={() => setDrawerOpen(true)}
              sx={{
                textTransform: "none",
                bgcolor: "var(--color-rodeo-black)",
                "&:hover": { bgcolor: "var(--color-rodeo-black)", opacity: 0.9 },
              }}
            >
              Record paid
            </Button>
          )}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: 2.5,
          }}
        >
          <Fact label="Organization">{p.organizationName ?? "—"}</Fact>
          <Fact label="Group">{p.groupName ?? "—"}</Fact>
          <Fact label="Credits">{Number(p.credits).toLocaleString("en-US")}</Fact>
          <Fact label="Charged">{money(p.netPrice ?? p.price, p.currency)}</Fact>
          <Fact label="Purchased">{formatDateTime(p.createdAt)}</Fact>
          <Fact label="Purchased by">{p.purchasedByName ?? "—"}</Fact>
          <Fact label="Card">
            {p.cardBrand ? `${p.cardBrand} ···· ${p.cardLast4 ?? ""}`.trim() : "—"}
          </Fact>
          <Fact label="Payout received">{p.payoutAt ? formatDateTime(p.payoutAt) : "Not yet"}</Fact>
          <Fact label="Stripe fee">{money(p.stripeFee, p.currency)}</Fact>
          <Fact label="Net to bank">{money(p.payoutNet, p.currency)}</Fact>
          <Fact label="PaymentIntent">
            <Box component="span" sx={{ fontSize: 12.5, wordBreak: "break-all" }}>
              {p.stripePaymentIntentId ?? "—"}
            </Box>
          </Fact>
          <Fact label="Payout reference">
            <Box component="span" sx={{ fontSize: 12.5, wordBreak: "break-all" }}>
              {p.stripePayoutId ?? "—"}
            </Box>
          </Fact>
        </Box>

        {isClearing && (
          <Typography sx={{ fontSize: 12.5, color: "var(--color-graphite)", mt: 2.5 }}>
            The card cleared and the credits are live, but Stripe's payout hasn't been seen. Stripe
            holds card funds for about two business days, so this is normal shortly after a purchase
            — record it by hand only if the payout has landed and the webhook didn't fire.
          </Typography>
        )}
      </Box>

      <RelatedJournalEntries sourceType={SOURCE_TYPE_PURCHASE} sourceId={p.id} />

      <PurchaseActivityLog purchaseId={p.id} />

      {isClearing && canRecord && (
        <RecordPayoutDrawer
          subject={`${p.purchaseNumber ?? `Purchase #${p.id}`} · ${p.organizationName ?? "—"}`}
          grossUsd={Number(p.netPrice ?? p.price ?? 0)}
          currency={p.currency}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onSubmit={(payload) => record.mutateAsync(payload)}
          isPending={record.isPending}
        />
      )}
    </Box>
  );
}
